<template>
  <div>
    <FormSubmitPopup
    :title="title"
    v-model="value"
    :rightMethod="onSubmitted"
    @popupClose="$emit('input', false)"
    :rightButtonText="$t('confirm')"
    hasAlert
    ref="formPopup"
    large>
    <template v-slot:input>
      <v-container py-0 fluid>
        <v-layout row>
          <v-flex xs12>
            <v-card class="template-header-card" width="100%">
              <v-card-text>
                <v-layout align-baseline justify-space-between wrap>
                  <v-flex class="title" md2>
                    {{ $t('templateInfo') }}:
                  </v-flex>
                  <v-flex md10>
                    <v-layout justify-space-between wrap>
                      <v-flex md3 xs12>
                        <v-text-field
                          hide-details
                          class="required_field"
                          :label="$t('templateName')"
                          v-model.trim="name"
                          :rules="[fieldIsRequired('template')]"
                        ></v-text-field>
                      </v-flex>
                      <v-flex md3 xs12>
                        <v-autocomplete
                          hide-details
                          :items="businessTypeOptions"
                          item-text="text"
                          item-value="value"
                          :label="$t('shippingType')"
                          v-model="businessType"
                          :rules="[fieldIsRequired('shipping type')]"
                          class="ml-3"
                        ></v-autocomplete>
                      </v-flex>
                      <v-flex md6 xs12>
                        <div class="template-header-hint">{{ $t('templateApplyDescription') }}</div>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>

        <v-layout row>
          <v-flex md6>
            <v-card width="100%">
              <v-card-text>
                <v-layout align-baseline>
                  <v-flex class="title" md3>{{ $t('sender') }}: </v-flex>
                  <v-flex md4>
                    <vite-autocomplete
                      hide-details
                      :items="addresses"
                      return-object
                      :label="$t('addressName')"
                      v-model="fromTemplate"
                      item-text="siteName"
                      item-value="siteName"
                    ></vite-autocomplete>
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-layout justify-space-between>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          :label="$t('fullName')"
                          v-model.trim="from.fullName"
                          :rules="[fieldIsRequired('Full name'), fieldCharLimit('Sender name', 35)]"
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          :label="$t('phone')"
                          v-model.trim="from.phone"
                        ></v-text-field>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <v-flex xs4>
                    <v-text-field
                      hide-details
                      :label="$t('companyName')"
                      v-model.trim="from.company"
                    ></v-text-field>
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-text-field
                      hide-details
                      :label="`${$t('address')} 1`"
                      :rules="[fieldIsRequired('Address'), fieldCharLimit('Address1', 50)]"
                      v-model.trim="from.address1"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs4>
                    <v-text-field
                      hide-details
                      :label="`${$t('address')} 2`"
                      :rules="[fieldCharLimit('Address2', 50)]"
                      v-model.trim="from.address2"
                    ></v-text-field>
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-layout justify-space-between>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          :label="$t('city')"
                          v-model.trim="from.city"
                          :rules="[fieldIsRequired('City')]"
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs5>
                        <v-autocomplete
                          v-if="fromStateOptions.length"
                          hide-details
                          :items="fromStateOptions"
                          :label="$t('state')"
                          v-model="from.state"
                          :rules="fromStateRules"
                        ></v-autocomplete>
                        <v-text-field
                          v-else
                          hide-details
                          :label="$t('state')"
                          v-model.trim="from.state"
                          :rules="fromStateRules"
                        ></v-text-field>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <v-flex xs4>
                    <v-text-field
                      hide-details
                      :label="$t('zipCode')"
                      v-model.trim="from.zipCode"
                      :rules="[fieldIsRequired('Zip code'), v => validateZipCodeBasic(v, from.countryCode)]"
                    ></v-text-field>
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-text-field
                      hide-details
                      :label="$t('email')"
                      v-model.trim="from.email"
                      :rules="[v => !v || fieldIsEmail()(v)]"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs4>
                    <v-autocomplete
                      hide-details
                      :items="fromCountryOptions"
                      item-text="name"
                      item-value="value"
                      :label="$t('country')"
                      v-model="from.countryCode"
                      :rules="fromCountryRules"
                    ></v-autocomplete>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-flex>

          <v-flex md6>
            <v-card width="100%">
              <v-card-text>
                <v-layout align-baseline>
                  <v-flex class="title" md3>{{ $t('recipient') }}: </v-flex>
                  <v-flex md4>
                    <vite-autocomplete
                      hide-details
                      :items="addresses"
                      return-object
                      :label="$t('addressName')"
                      v-model="toTemplate"
                      item-text="siteName"
                      item-value="siteName"
                    ></vite-autocomplete>
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-layout justify-space-between>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          :label="$t('fullName')"
                          v-model.trim="to.fullName"
                          :rules="[fieldIsRequired('Full name'), fieldCharLimit('Full name', 35)]"
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          :label="$t('phone')"
                          v-model.trim="to.phone"
                          :rules="businessType === 'domestic' ? [fieldIsRequired('Phone'), validatePhoneBasic] : []"
                        ></v-text-field>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <v-flex xs4>
                    <v-text-field
                      hide-details
                      :label="$t('companyName')"
                      v-model.trim="to.company"
                    ></v-text-field>
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-text-field
                      hide-details
                      :label="`${$t('address')} 1`"
                      :rules="[fieldIsRequired('Address'), fieldCharLimit('Address1', 50)]"
                      v-model.trim="to.address1"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs4>
                    <v-text-field
                      hide-details
                      :label="`${$t('address')} 2`"
                      :rules="[fieldCharLimit('Address2', 50)]"
                      v-model.trim="to.address2"
                    ></v-text-field>
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-layout justify-space-between>
                      <v-flex xs5>
                        <v-text-field
                          hide-details
                          :label="$t('city')"
                          v-model.trim="to.city"
                          :rules="[fieldIsRequired('City')]"
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs5>
                        <v-autocomplete
                          v-if="toStateOptions.length"
                          hide-details
                          :items="toStateOptions"
                          :label="$t('state')"
                          v-model="to.state"
                          :rules="toStateRules"
                        ></v-autocomplete>
                        <v-text-field
                          v-else
                          hide-details
                          :label="$t('state')"
                          v-model.trim="to.state"
                          :rules="toStateRules"
                        ></v-text-field>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <v-flex xs4>
                    <v-text-field
                      hide-details
                      :label="$t('zipCode')"
                      v-model.trim="to.zipCode"
                      :rules="[fieldIsRequired('Zip code'), v => validateZipCodeBasic(v, to.countryCode)]"
                    ></v-text-field>
                  </v-flex>
                </v-layout>
                <v-layout justify-space-between>
                  <v-flex xs7>
                    <v-text-field
                      hide-details
                      :label="$t('email')"
                      v-model.trim="to.email"
                      :rules="[v => !v || fieldIsEmail()(v)]"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs4>
                    <v-autocomplete
                      hide-details
                      :items="toCountryOptions"
                      item-text="name"
                      item-value="value"
                      :label="$t('country')"
                      v-model="to.countryCode"
                      :rules="toCountryRules"
                    ></v-autocomplete>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
        <v-layout row v-if="isSupportDangerousGoods">
          <v-flex>
            <v-card width="100%">
              <v-card-text>
                <HazardousCommodities
                  v-model="hazMatsInfo"
                  :dangerousGoods.sync="dangerousGoods"
                  :offerorName.sync="offerorName"
                  :emergencyContactNumber.sync="emergencyContactNumber"
                  :dryIceWeight.sync="dryIceDetail.weight"
                  :dryIceUnit.sync="dryIceDetail.unit"
                  :filterItems="['', 'LITHIUM_BATTERY']"
                  :isPrintSafetyDocument.sync="isPrintSafetyDocument"
                  :signatureCode.sync="signatureCode"
                />
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex>
            <v-card width="100%" height="100%">
              <v-card-text>
                <v-layout class="title">
                  {{ $t('labelDetails') }}:
                </v-layout>
                <v-layout justify-space-between align-baseline wrap>
                  <v-flex md3 xs5>
                    <v-autocomplete
                      hide-details
                      :items="displayShippingServices()"
                      item-text="text"
                      item-value="value"
                      :menu-props="{returnValue:'value'}"
                      v-model="shippingService"
                      :rules="[fieldIsRequired('service'), checkShipForm()]"
                      :label="$t('shippingService')"
                    ></v-autocomplete>
                  </v-flex>
                  <v-flex md2 xs5>
                    <v-select
                      hide-details
                      :label="$t('signature')"
                      :items="signatureOptions"
                      item-text="text"
                      :menu-props="{returnValue:'value'}"
                      v-model="signature"
                    ></v-select>
                  </v-flex>
                  <v-flex md2 xs5>
                    <v-select
                      hide-details
                      :disabled="!isSupportLabelStockType"
                      :label="$t('labelStockType')"
                      :items="labelStockTypes"
                      item-text="text"
                      :menu-props="{returnValue:'value'}"
                      v-model="labelStockType"
                    ></v-select>
                  </v-flex>
                </v-layout>

                <v-data-table
                  :headers="packageHeaders"
                  :items="packages"
                  hide-actions
                  class="elevation-1 mt-2"
                  item-key="_key"
                  :expand="true"
                  caption
                >
                  <template v-slot:headerCell="props">
                    <span v-if="props.header.value === 'action'">
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn color="primary" flat small icon v-on="on" @click="addPackage">
                            <v-icon>add</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('addPackage') }}</span>
                      </v-tooltip>
                    </span>
                    <span v-else>{{ props.header.text }}</span>
                  </template>
                  <template v-slot:items="props">
                    <td class="text-xs-center">
                      <v-btn v-if="props.index > 0" color="primary" flat small icon @click="removePackage(props.index)">
                        <v-icon>delete</v-icon>
                      </v-btn>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item"
                        hide-details
                        v-model.number="props.item.qty"
                        :rules="[
                          fieldIsRequired('qty'),
                          fieldIsOverZero('qty'),
                          fieldIsInteger('qty')
                        ]"
                      ></v-text-field>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item"
                        hide-details
                        v-model.number="props.item.weight"
                        :rules="[
                          fieldIsRequired('weight'),
                          fieldIsOverZero('weight')
                        ]"
                        :suffix="isMeasurementMetric ? 'kg' : 'Lbs'"
                      ></v-text-field>
                    </td>
                    <td class="text-xs-left">
                      <v-layout align-center>
                        <v-flex xs4 class="mr-3">
                          <v-text-field
                            class="table-item"
                            hide-details
                            v-model.number="props.item.length"
                            :suffix="isMeasurementMetric ? 'cm' : 'in'"
                            :rules="[
                              fieldIsRequired('length'),
                              fieldIsOverZero('length')
                            ]"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs4 class="mr-3">
                          <v-text-field
                            class="table-item"
                            hide-details
                            v-model.number="props.item.width"
                            :suffix="isMeasurementMetric ? 'cm' : 'in'"
                            :rules="[
                              fieldIsRequired('width'),
                              fieldIsOverZero('width')
                            ]"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs4>
                          <v-text-field
                            class="table-item"
                            hide-details
                            v-model.number="props.item.height"
                            :suffix="isMeasurementMetric ? 'cm' : 'in'"
                            :rules="[
                              fieldIsRequired('height'),
                              fieldIsOverZero('height')
                            ]"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs1>
                          <PackagingSelector
                            :packagings="packagings"
                            @select="item => onSelectPackaging(item, props.item)"
                          />
                        </v-flex>
                      </v-layout>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item"
                        hide-details
                        v-model.number="props.item.memo"
                      ></v-text-field>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item"
                        hide-details
                        :disabled="!enableInsured"
                        v-model.number="props.item.insuredValue"
                        :rules="[fieldIsNoLessThanZero('value')]"
                      ></v-text-field>
                    </td>
                    <td class="text-xs-center" v-if="isLithiumBatteryDangerousGoods">
                      <v-layout align-center style="cursor: pointer;" justify-center :id="`expand-icon-${props.item._key || props.index}`" @click="toggleExpand(props)">
                        <v-icon color="primary" class="mr-1">battery_charging_full</v-icon>
                        <v-icon>expand_more</v-icon>
                      </v-layout>
                    </td>
                  </template>
                  <template v-slot:expand="props">
                    <LithiumBatteryDangerousGoods
                      v-if="isLithiumBatteryDangerousGoods || (props.item.isLithiumBattery && props.item.lithiumBatteryOptions && props.item.lithiumBatteryOptions.length > 0)"
                      :props="props"
                      :lithiumBatteryOptionItems="lithiumBatteryOptionItems"
                      @applyAll="applyAllLithiumBattery"
                    />
                  </template>
                </v-data-table>

              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>

        <v-layout row v-if="businessType === 'export'">
          <v-flex>
            <v-card width="100%">
              <v-card-text>
                <v-layout class="title" align-baseline justify-space-between>
                  <v-flex md2 xs4>
                    {{ $t('commodity') }}:
                  </v-flex>
                  <v-flex md4 xs8>
                    <v-autocomplete
                      hide-details
                      :items="commodityTemplates"
                      return-object
                      item-text="name"
                      :label="$t('template')"
                      v-model="selectedCommodityTemplate"
                      @change="onSelectCommodityTemplate"
                    ></v-autocomplete>
                  </v-flex>
                </v-layout>

                <v-data-table
                  :headers="commodityHeaders"
                  :items="commodities"
                  hide-actions
                  class="elevation-1 mt-2"
                  item-key="index"
                >
                  <template v-slot:headerCell="props">
                    <span v-if="props.header.value === 'action'">
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn color="primary" flat small icon v-on="on" @click="addCommodity">
                            <v-icon>add</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('addCommodity') }}</span>
                      </v-tooltip>
                    </span>
                    <span v-else>{{ props.header.text }}</span>
                  </template>
                  <template v-slot:items="props">
                    <td class="text-xs-center">
                      <v-btn v-if="props.index > 0" color="primary" flat small icon @click="removeCommodity(props.index)">
                        <v-icon>delete</v-icon>
                      </v-btn>
                    </td>
                    <td class="text-xs-left">
                      <v-autocomplete
                        class="table-item"
                        hide-details
                        :items="mergedCommoditiesList"
                        item-text="scheduleBDescription"
                        return-object
                        :value="props.item.selectedCommodity"
                        :rules="[fieldIsRequired($t('commodity'))]"
                        @change="onSelectCommodity(props.index, $event)"
                      ></v-autocomplete>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item"
                        hide-details
                        v-model.trim="props.item.description"
                        :rules="[fieldIsRequired($t('description')), fieldCharLimit('description', 100)]"
                        @input="onCommodityFieldInput(props.item, 'description', props.item.description)"
                      ></v-text-field>
                    </td>
                    <td class="text-xs-left">
                      <v-autocomplete
                        class="table-item"
                        hide-details
                        :items="countryCodeArray"
                        item-text="name"
                        item-value="value"
                        v-model="props.item.countryOfOrigin"
                        :rules="[fieldIsRequired($t('countryOfOrigin'))]"
                      ></v-autocomplete>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item"
                        hide-details
                        v-model.trim="props.item.harmonizedCode"
                        :rules="[fieldIsRequired($t('hsCode')), validateHsCode]"
                      ></v-text-field>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item"
                        hide-details
                        v-model.number="props.item.quantity"
                        :rules="[fieldIsRequired($t('qty')), fieldIsInteger($t('qty')), fieldIsOverZero($t('qty'))]"
                        @input="onCommodityFieldInput(props.item, 'quantity', props.item.quantity)"
                      ></v-text-field>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item"
                        hide-details
                        v-model.trim="props.item.unit"
                        :rules="[fieldIsRequired($t('unit'))]"
                      ></v-text-field>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item"
                        hide-details
                        v-model.number="props.item.price"
                        :rules="[fieldIsNoLessThanZero($t('price'))]"
                        @input="onCommodityFieldInput(props.item, 'price', props.item.price)"
                      ></v-text-field>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item"
                        hide-details
                        prefix="$"
                        :disabled="true"
                        :value="computeCustomsValue({ quantity: props.item.quantity, price: props.item.price })"
                        :rules="[fieldIsNoLessThanZero($t('totalCustomsValue($)'))]"
                      ></v-text-field>
                    </td>
                    <td class="text-xs-left">
                      <v-text-field
                        class="table-item"
                        hide-details
                        v-model.number="props.item.commodityWeight"
                        :rules="[fieldIsRequired($t('weight')), fieldIsOverZero($t('weight'))]"
                      ></v-text-field>
                    </td>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-container>
    </template>
  </FormSubmitPopup>
  </div>
</template>

<script>
import FormSubmitPopup from './FormSubmitPopup'
import { timeTools, checkRules, cloneDeep, measurementTools, labelServiceComponentTools } from '@/utils/tools'
import HazardousCommodities from './HazardousCommodities'
import PackagingSelector from './PackagingSelector'
import LithiumBatteryDangerousGoods from './LithiumBatteryDangerousGoods.vue'

const METAL_CONTAINED = 'METAL_CONTAINED'
const METAL_PACKED = 'METAL_PACKED'
const ION_CONTAINED = 'ION_CONTAINED'
const ION_PACKED = 'ION_PACKED'
export default {
  name: 'LabelTemplatePopupForLabel',
  components: {
    FormSubmitPopup,
    HazardousCommodities,
    PackagingSelector,
    LithiumBatteryDangerousGoods
  },
  mixins: [
    checkRules,
    timeTools,
    measurementTools,
    labelServiceComponentTools
  ],
  data () {
    return {
      title: this.$t('labelTemplate'),
      packaging: '',
      signature: '',
      newSenderName: '',
      from: {
        fullName: '',
        phone: '',
        company: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        email: '',
        countryCode: 'US'
      },
      to: {
        fullName: '',
        phone: '',
        company: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        email: '',
        countryCode: 'US'
      },
      businessType: 'domestic',
      shippingService: '',
      fromTemplate: {},
      toTemplate: {},
      name: '',
      dangerousGoods: '',
      hazMatsInfo: [],
      offerorName: '',
      emergencyContactNumber: '',
      dryIceDetail: {
        weight: '',
        unit: ''
      },
      isPrintSafetyDocument: true,
      signatureCode: null,
      selectedCommodityTemplate: null,
      commodities: [
        {
          commodity: '',
          description: '',
          countryOfOrigin: 'US',
          harmonizedCode: '',
          quantity: 1,
          unit: '',
          price: 0,
          // customsValue: 0,
          commodityWeight: 0,
          selectedCommodity: null
        }
      ],
      labelStockType: 'STOCK_4X6',
      // New packages array structure
      packages: [
        {
          qty: 1,
          length: 0,
          width: 0,
          height: 0,
          weight: 0,
          insuredValue: 0
        }
      ],
      forceExpandState: null
    }
  },
  watch: {
    isSupportLabelStockType: {
      handler (value) {
        if (value) return
        this.labelStockType = 'STOCK_4X6'
      },
      immediate: true
    },
    signatureOptions (value) {
      if (!Array.isArray(value) || value.length < 1) {
        this.signature = ''
        return
      }
      let defaultValue = value[0].value || ''
      for (let option of value) {
        if (option.default) defaultValue = option.value
        if (option.value === this.signature) return
      }
      this.signature = defaultValue
    },
    fromTemplate (value) {
      if (!value || typeof value !== 'object') return
      this.from = {
        ...this.from,
        ...cloneDeep(value)
      }
    },
    toTemplate (value) {
      if (!value || typeof value !== 'object') return
      this.to = {
        ...this.to,
        ...cloneDeep(value)
      }
    },
    shippingService (value, oldValue) {
      if (value === oldValue) return
      if (!this.enableInsured) {
        this.packages.forEach(pkg => {
          pkg.insuredValue = 0
        })
      }
      const isSupportDangerousGoods = this.labelServices.some(service => {
        const labelService = service.serviceTypes.find(type => type.value === this.shippingService)
        return labelService
          ? labelService.hasOwnProperty('isSupportDangerousGoods')
            ? labelService.isSupportDangerousGoods
            : service.isSupportDangerousGoods
          : false
      })
      if (!isSupportDangerousGoods) {
        this.isPrintSafetyDocument = true
        this.dangerousGoods = ''
        this.signatureCode = ''
        this.packages.forEach(pkg => {
          pkg.isLithiumBattery = false
          pkg.lithiumBatteryOptions = []
        })
      }
    },
    // businessType (value) {
    //   this.ensureShippingServiceForBusinessType()
    //   if (value === 'export') {
    //     this.to.countryCode = ''
    //     this.loadExportCommodityReferences()
    //     return
    //   }
    //   this.to.countryCode = 'US'
    // },
    shippingServices () {
      this.ensureShippingServiceForBusinessType()
    },
    packages (value) {
      // Initialize _key and lithium battery fields on packages
      value.forEach((pkg, index) => {
        if (!pkg._key) {
          this.$set(pkg, '_key', `pkg_${Date.now()}_${index}`)
        }
        if (pkg.isLithiumBattery === undefined) {
          this.$set(pkg, 'isLithiumBattery', false)
        }
        if (!pkg.lithiumBatteryOptions) {
          this.$set(pkg, 'lithiumBatteryOptions', [])
        }
      })
    },
    dangerousGoods: {
      handler (newVal, oldVal) {
        if (newVal === oldVal) return
        // Auto-expand/collapse rows when switching dangerous goods type
        if (newVal === 'LITHIUM_BATTERY') {
          this.forceExpandState = 'expand'
          this.$nextTick(() => {
            this.$nextTick(() => {
              this.packages.forEach((pkg, index) => {
                const iconId = `expand-icon-${pkg._key || index}`
                const icon = document.getElementById(iconId)
                if (icon) icon.click()
              })
              this.$nextTick(() => {
                this.forceExpandState = null
              })
            })
          })
        } else if (oldVal === 'LITHIUM_BATTERY' && newVal !== 'LITHIUM_BATTERY') {
          this.forceExpandState = 'collapse'
          this.$nextTick(() => {
            this.$nextTick(() => {
              this.packages.forEach((pkg, index) => {
                pkg.isLithiumBattery = false
                pkg.lithiumBatteryOptions = []
                const iconId = `expand-icon-${pkg._key || index}`
                const icon = document.getElementById(iconId)
                if (icon) icon.click()
              })
              this.$nextTick(() => {
                this.forceExpandState = null
              })
            })
          })
        }
        // Initialize lithium battery fields and _key on packages
        this.packages.forEach((pkg, index) => {
          // Initialize _key if not exists
          if (!pkg._key) {
            this.$set(pkg, '_key', `pkg_${Date.now()}_${index}`)
          }
          // Initialize lithium battery fields
          if (pkg.isLithiumBattery === undefined) {
            this.$set(pkg, 'isLithiumBattery', false)
          }
          if (!pkg.lithiumBatteryOptions) {
            this.$set(pkg, 'lithiumBatteryOptions', [])
          }
        })
      },
      immediate: true
    }
  },
  computed: {
    lithiumBatteryOptionItems () {
      return [
        {
          value: METAL_CONTAINED,
          label: this.$t('metalContainedInEquipment'),
          tooltip: this.$t('metalContainedTooltip')
        },
        {
          value: METAL_PACKED,
          label: this.$t('metalPackedWithEquipment'),
          tooltip: this.$t('metalPackedTooltip')
        },
        {
          value: ION_CONTAINED,
          label: this.$t('ionContainedInEquipment'),
          tooltip: this.$t('ionContainedTooltip')
        },
        {
          value: ION_PACKED,
          label: this.$t('ionPackedWithEquipment'),
          tooltip: this.$t('ionPackedTooltip')
        }
      ]
    },
    isLithiumBatteryDangerousGoods () {
      return this.dangerousGoods === 'LITHIUM_BATTERY'
    },
    currentShippingService () {
      return this.labelServices.find(service =>
        service.serviceTypes.some(serviceType => this.shippingService === serviceType.value)
      ) || {}
    },
    isSupportLabelStockType () {
      return this.currentShippingService.isSupportLabelStockType
    },
    isSupportDangerousGoods () {
      const isSupportDangerousGoods = this.labelServices.some(service => {
        const serviceType = service.serviceTypes.find(serviceType => this.shippingService === serviceType.value)
        return serviceType && serviceType.isSupportDangerousGoods
      })
      return this.visibleBetaFeatures.includes('hazmat') && isSupportDangerousGoods
    },
    visibleBetaFeatures () {
      return this.$store.getters.visibleBetaFeatures
    },
    addresses () {
      return this.$store.getters.tenantAddresses
    },
    packagings () {
      return this.$store.getters.tenantPackagingsWithWarehousePackagings
    },
    labelServices () {
      if (this.businessType === 'export') return this.$store.getters.internationalLabelServices || []
      if (this.businessType === 'import') return this.$store.getters.importToUSLabelServices || []
      return this.$store.getters.labelServices || []
    },
    shippingServices () {
      return this.labelServices
        .reduce((services, { serviceTypes = [] }) => [
          ...services,
          ...serviceTypes,
          { divider: true }
        ], [])
    },
    serviceSettings () {
      return this.getLabelServiceSettingByCarrierAndServiceTypeValue(this.carrier, this.shippingService)
    },
    enableInsured () {
      return this.serviceSettings.enableInsured
    },
    carrier () {
      return (this.labelServices.find(service =>
        service.serviceTypes.find(serviceType =>
          this.shippingService === serviceType.value
        )
      ) || {}).carrier
    },
    signatureOptions () {
      return this.serviceSettings.signatureOptions || []
    },
    isMeasurementMetric () {
      return !!this.$store.getters.isMeasurementMetric
    },
    labelStockTypes () {
      return this.$store.getters.labelStockTypes
    },
    packageHeaders () {
      const headers = [
        { text: this.$t('action'), value: 'action', align: 'center', sortable: false },
        { text: this.$t('packageQty'), value: 'qty', align: 'left', sortable: false },
        { text: this.$t('weight'), value: 'weight', align: 'left', sortable: false },
        { text: `${this.$t('length')} / ${this.$t('width')} / ${this.$t('height')}`, value: 'packaging', align: 'left', sortable: false },
        { text: this.$t('memo'), value: 'memo', align: 'left', sortable: false },
        { text: this.$t('declaredValue'), value: 'insuredValue', align: 'left', sortable: false }
      ]
      // Add expand column for lithium battery dangerous goods
      if (this.isLithiumBatteryDangerousGoods) {
        headers.push(
          { text: '', value: '', align: 'center', sortable: false, width: '80px' }
        )
      }
      return headers
    },
    businessTypeOptions () {
      return [
        { text: 'Domestic', value: 'domestic' },
        { text: 'Export', value: 'export' },
        { text: 'Import', value: 'import' }
      ]
    },
    stateAbbrevs () {
      return this.$store.getters.stateAbbrevs || []
    },
    countryStatesMap () {
      return this.$store.getters.countryStatesMap || {}
    },
    fromStateOptions () {
      if (['domestic', 'export'].includes(this.businessType)) {
        return this.normalizeCountryCode(this.from.countryCode) === 'US' ? this.stateAbbrevs : []
      }
      const countryCode = this.normalizeCountryCode(this.from.countryCode)
      if (countryCode === 'US') return this.stateAbbrevs
      return Object.values(this.countryStatesMap[countryCode] || {})
    },
    toStateOptions () {
      if (this.businessType === 'export') return []
      const countryCode = this.normalizeCountryCode(this.to.countryCode)
      if (countryCode === 'US') return this.stateAbbrevs
      return Object.values(this.countryStatesMap[countryCode] || {})
    },
    isStateRequiredForFrom () {
      return this.businessType === 'domestic' || this.fromStateOptions.length > 0
    },
    isStateRequiredForTo () {
      if (this.businessType === 'export') return false
      return this.businessType === 'domestic' || this.toStateOptions.length > 0 || this.normalizeCountryCode(this.to.countryCode) === 'US'
    },
    importAllowedCountries () {
      return this.businessType === 'import' ? (this.currentShippingService.allowedCountries || []) : []
    },
    countryCodeArray () {
      return this.$store.getters.countryCodeArray || []
    },
    fromCountryOptions () {
      if (!this.importAllowedCountries.length) return this.countryCodeArray
      return this.countryCodeArray.filter(({ value }) => this.importAllowedCountries.includes(value))
    },
    toCountryOptions () {
      if (this.businessType !== 'import') return this.countryCodeArray
      return [{
        name: this.$t('unitedStates'),
        value: 'US'
      }]
    },
    fromStateRules () {
      return this.buildStateRules(this.fromStateOptions, this.isStateRequiredForFrom, this.formatAddressFieldLabel('sender', 'state'))
    },
    toStateRules () {
      return this.buildStateRules(this.toStateOptions, this.isStateRequiredForTo, this.formatAddressFieldLabel('recipient', 'state'))
    },
    fromCountryRules () {
      const rules = [this.fieldIsRequired('Country')]
      if (this.importAllowedCountries.length) {
        rules.push(v => this.importAllowedCountries.includes(v) || this.$t('fieldMustBeOneOf', {
          fieldName: this.formatAddressFieldLabel('sender', 'country'),
          options: this.importAllowedCountries.join(', ')
        }))
      }
      return rules
    },
    toCountryRules () {
      const rules = [this.fieldIsRequired('Country')]
      if (this.businessType === 'import') {
        rules.push(v => this.normalizeCountryCode(v) === 'US' || this.$t('fieldMustEqual', {
          fieldName: this.formatAddressFieldLabel('recipient', 'country'),
          value: this.$t('unitedStates')
        }))
      }
      return rules
    },
    commoditiesList () {
      return this.$store.getters.commoditiesList || []
    },
    commodityTemplates () {
      return this.$store.getters.commodityTemplates || []
    },
    mergedCommoditiesList () {
      const customOption = {
        scheduleBDescription: 'Commodity Desc',
        description: 'Commodity Desc',
        scheduleBCode: '',
        harmonizedCode: '',
        countryOfOrigin: 'US',
        unit: '',
        _isCustom: true
      }
      const mapped = (this.commoditiesList || []).map(item => ({
        scheduleBDescription: item.itemDesc || item.commodityDetail || '',
        description: item.itemDesc || item.commodityDetail || '',
        scheduleBCode: item.scheduleBCode || '',
        harmonizedCode: item.scheduleBCode || '',
        countryOfOrigin: item.origin === 'F' ? (item.countryCode || 'CN') : 'US',
        unit: item.unit || ''
      }))
      return [customOption, ...mapped]
    },
    commodityHeaders () {
      const totalCustomsValue = this.commodities.reduce((acc, item) => acc + (this.computeCustomsValue({ quantity: item.quantity, price: item.price }) || 0), 0)
      return [
        { text: this.$t('action'), value: 'action', align: 'center', sortable: false },
        { text: this.$t('commodity'), value: 'commodity', align: 'left', sortable: false },
        { text: this.$t('description'), value: 'description', align: 'left', sortable: false },
        { text: this.$t('countryOfOrigin'), value: 'countryOfOrigin', align: 'left', sortable: false },
        { text: this.$t('hsCode'), value: 'harmonizedCode', align: 'left', sortable: false },
        { text: this.$t('qty'), value: 'quantity', align: 'left', sortable: false },
        { text: this.$t('unit'), value: 'unit', align: 'left', sortable: false },
        { text: this.$t('price'), value: 'price', align: 'left', sortable: false },
        { text: `${this.$t('totalCustomsValue($)')}($${totalCustomsValue.toLocaleString()})`, value: 'customsValue', align: 'left', sortable: false },
        { text: this.$t('weight'), value: 'commodityWeight', align: 'left', sortable: false }
      ]
    }
  },
  mounted () {
    if (this.initTemplate) {
      const {
        isMeasurementMetric,
        weightLb = 0,
        packages,
        packaging,
        to = {},
        from = {},
        businessType = 'domestic',
        isSupportDangerousGoods,
        signatureCode,
        ...rest
      } = this.initTemplate
      if (packages && Array.isArray(packages) && packages.length > 0) {
        if (signatureCode && typeof signatureCode === 'string') {
          rest.signatureCode = {
            downloadURL: signatureCode
          }
        } else {
          rest.signatureCode = signatureCode || null
        }
        Object.assign(this, cloneDeep(rest))
        this.packages = packages.map(pkg => ({
          qty: pkg.qty || 1,
          length: pkg.length || 0,
          width: pkg.width || 0,
          height: pkg.height || 0,
          weight: this.convertWeight(pkg.weight || 0, isMeasurementMetric),
          memo: pkg.memo || '',
          isLithiumBattery: pkg.isLithiumBattery || false,
          lithiumBatteryOptions: pkg.lithiumBatteryOptions || [],
          insuredValue: pkg.insuredValue || pkg.declaredValue || 0
        }))
      } else {
        Object.assign(this, cloneDeep(rest))
        const pkg = {
          qty: 1,
          length: rest.length || 0,
          width: rest.width || 0,
          height: rest.height || 0,
          weight: this.convertWeight(weightLb, isMeasurementMetric),
          insuredValue: rest.insuredValue || rest.declaredValue || 0
        }
        this.packages = [pkg]

        // Apply packaging if exists
        if (packaging) {
          const targetPackaging = this.packagings.find(item => item.id === packaging)
          if (targetPackaging) {
            const {height, length, width, isMeasurementMetric: isPackagingMeasurementMetric = false} = targetPackaging
            Object.assign(pkg, this.convertPackagingSize({height, length, width}, isPackagingMeasurementMetric))
          }
        }
      }

      this.from = {
        ...this.from,
        ...cloneDeep(from || {})
      }
      this.to = {
        ...this.to,
        ...cloneDeep(to || {})
      }
      this.businessType = businessType
      this.commodities = this.normalizeTemplateCommodities(this.initTemplate.commodities)
    }
    if (this.businessType === 'export') {
      this.loadExportCommodityReferences()
      this.commodities = this.normalizeTemplateCommodities(this.commodities)
    }
    this.ensureShippingServiceForBusinessType()
    this.$watch('businessType', (value) => {
      this.ensureShippingServiceForBusinessType()
      if (value === 'export') {
        this.to.countryCode = ''
        this.loadExportCommodityReferences()
        return
      }
      this.to.countryCode = 'US'
    })
  },
  methods: {
    applyAllLithiumBattery (item) {
      this.packages.forEach(pkg => {
        this.$set(pkg, 'lithiumBatteryOptions', [...item.lithiumBatteryOptions])
        this.$set(pkg, 'isLithiumBattery', item.isLithiumBattery)
      })
    },
    addPackage () {
      const newPackage = {
        qty: 1,
        length: 0,
        width: 0,
        height: 0,
        weight: 0,
        memo: '',
        insuredValue: 0
      }
      newPackage._key = `pkg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      newPackage.isLithiumBattery = false
      newPackage.lithiumBatteryOptions = []
      this.packages.push(newPackage)
    },
    toggleExpand (props) {
      if (this.forceExpandState === 'expand') {
        props.expanded = true
      } else if (this.forceExpandState === 'collapse') {
        props.expanded = false
      } else {
        props.expanded = !props.expanded
      }
    },
    removePackage (index) {
      if (this.packages.length > 1) {
        this.packages.splice(index, 1)
      }
    },
    onSelectPackaging (e, item) {
      const {height, length, width, weight, isMeasurementMetric = false} = e
      Object.assign(item, this.convertPackagingSize({height, length, width}, isMeasurementMetric))
      if (weight) {
        this.$set(item, 'weight', this.convertWeight(weight, isMeasurementMetric))
      }
    },
    loadExportCommodityReferences () {
      if (!this.commoditiesList || this.commoditiesList.length === 0) {
        this.$store.dispatch('getCommoditiesList', {})
      }
      if (!this.commodityTemplates || this.commodityTemplates.length === 0) {
        this.$store.dispatch('getCommodityTemplates')
      }
    },
    getEmptyCommodity () {
      return {
        commodity: '',
        description: '',
        countryOfOrigin: 'US',
        harmonizedCode: '',
        quantity: 1,
        unit: '',
        price: 0,
        // customsValue: 0,
        commodityWeight: 0,
        selectedCommodity: null
      }
    },
    normalizeTemplateCommodities (commodities = []) {
      if (!Array.isArray(commodities) || commodities.length < 1) {
        return [this.getEmptyCommodity()]
      }
      return commodities.map(item => {
        const selectedCommodity = this.mergedCommoditiesList.find(c =>
          c.scheduleBCode && c.scheduleBCode === (item.scheduleBCode || item.harmonizedCode || '')
        ) || this.mergedCommoditiesList.find(c => c._isCustom)
        const quantity = Number(item.quantity) || 0
        const price = Number(item.price) || 0
        return {
          ...this.getEmptyCommodity(),
          ...item,
          commodity: item.commodity || item.scheduleBDescription || item.description || '',
          description: item.description || item.scheduleBDescription || '',
          harmonizedCode: item.harmonizedCode || item.scheduleBCode || '',
          countryOfOrigin: item.countryOfOrigin || 'US',
          unit: item.unit || item.unitOfMeasure || '',
          quantity,
          price,
          customsValue: this.computeCustomsValue({ quantity, price }),
          commodityWeight: Number(item.commodityWeight || item.weight) || 0,
          selectedCommodity
        }
      })
    },
    addCommodity () {
      this.commodities.push(this.getEmptyCommodity())
    },
    removeCommodity (index) {
      if (this.commodities.length > 1) {
        this.commodities.splice(index, 1)
      }
    },
    onSelectCommodityTemplate (template) {
      if (!template) return
      const templateData = typeof template === 'string'
        ? this.commodityTemplates.find(item => item.name === template)
        : template
      if (!templateData || !Array.isArray(templateData.commodities)) return
      this.commodities = this.normalizeTemplateCommodities(templateData.commodities)
    },
    onSelectCommodity (index, selectedItem) {
      const item = this.commodities[index]
      if (!item) return
      const selectedCommodity = selectedItem || this.mergedCommoditiesList.find(c => c._isCustom)
      this.$set(this.commodities, index, {
        ...item,
        selectedCommodity,
        commodity: selectedCommodity.scheduleBDescription || item.commodity || '',
        description: selectedCommodity.description || selectedCommodity.scheduleBDescription || item.description || '',
        harmonizedCode: selectedCommodity.harmonizedCode || selectedCommodity.scheduleBCode || item.harmonizedCode || '',
        countryOfOrigin: selectedCommodity.countryOfOrigin || item.countryOfOrigin || 'US',
        unit: selectedCommodity.unit || item.unit || ''
      })
    },
    onCommodityFieldInput (item, field, value) {
      item[field] = value
      // if (field === 'quantity' || field === 'price') {
      //   item.customsValue = this.computeCustomsValue(item)
      // }
    },
    computeCustomsValue (item) {
      const quantity = Number(item.quantity) || 0
      const price = Number(item.price) || 0
      return Math.ceil(quantity * price)
    },
    displayShippingServices () {
      if (this.businessType === 'export') {
        this._filteredFromStateShippingServices = this.shippingServices || []
        return this.shippingServices
      }
      return this.filterShippingServiceByFromState(this.shippingServices, this.from.state)
    },
    normalizeCountryCode (value) {
      return `${value || ''}`.trim().toUpperCase()
    },
    formatAddressFieldLabel (partyKey, fieldKey) {
      return `${this.$t(partyKey)} ${this.$t(fieldKey)}`
    },
    buildStateRules (stateOptions, required, fieldName) {
      const rules = []
      if (required) {
        rules.push(this.fieldIsRequired(fieldName))
      }
      if (stateOptions.length) {
        rules.push(v => !v || stateOptions.includes(v) || this.$t('fieldMustBeOneOf', {
          fieldName,
          options: stateOptions.join(', ')
        }))
      }
      return rules
    },
    validatePhoneBasic (value) {
      if (!value) return true
      const normalized = `${value}`.replace(/\s+/g, '')
      return /^(\d{10}$)|(^\d{3}-\d{3}-\d{4}$)/.test(normalized) || this.$t('fieldIsPhone')
    },
    validateZipCodeBasic (value, countryCode) {
      if (!value) return true
      if (`${countryCode || ''}`.toUpperCase() !== 'US') return true
      return this.fieldIsZip()(value)
    },
    validateHsCode (value) {
      if (!value) return true
      if (value.length < 6 || value.length > 15 || isNaN(+value)) {
        return this.$t('hsCodeValidation')
      }
      return true
    },
    validateAddressBasics () {
      const requiredFieldLabels = {
        fullName: 'fullName',
        address1: 'address',
        city: 'city',
        zipCode: 'zipCode',
        countryCode: 'country'
      }
      const validateAddress = (address, partyKey, { requireState = false, allowedStates = [] } = {}) => {
        for (const [field, fieldKey] of Object.entries(requiredFieldLabels)) {
          const value = address[field]
          if (value === undefined || value === null || `${value}`.trim() === '') {
            throw Error(this.$t('fieldIsRequired', {
              fieldName: this.formatAddressFieldLabel(partyKey, fieldKey)
            }))
          }
        }
        if (requireState && !`${address.state || ''}`.trim()) {
          throw Error(this.$t('fieldIsRequired', {
            fieldName: this.formatAddressFieldLabel(partyKey, 'state')
          }))
        }
        if (allowedStates.length && address.state && !allowedStates.includes(address.state)) {
          throw Error(this.$t('fieldMustBeOneOf', {
            fieldName: this.formatAddressFieldLabel(partyKey, 'state'),
            options: allowedStates.join(', ')
          }))
        }
        if (this.validateZipCodeBasic(address.zipCode, address.countryCode) !== true) {
          throw Error(this.$t('fieldIsZip'))
        }
        if (address.email && !this.fieldIsEmail()(address.email)) {
          throw Error(this.$t('fieldIsEmail'))
        }
      }
      validateAddress(this.from || {}, 'sender', {
        requireState: this.isStateRequiredForFrom,
        allowedStates: this.fromStateOptions
      })
      validateAddress(this.to || {}, 'recipient', {
        requireState: this.isStateRequiredForTo,
        allowedStates: this.toStateOptions
      })
      if (this.importAllowedCountries.length && !this.importAllowedCountries.includes(this.normalizeCountryCode(this.from.countryCode))) {
        throw Error(this.$t('fieldMustBeOneOf', {
          fieldName: this.formatAddressFieldLabel('sender', 'country'),
          options: this.importAllowedCountries.join(', ')
        }))
      }
      if (this.businessType === 'import' && this.normalizeCountryCode(this.to.countryCode) !== 'US') {
        throw Error(this.$t('fieldMustEqual', {
          fieldName: this.formatAddressFieldLabel('recipient', 'country'),
          value: this.$t('unitedStates')
        }))
      }
      if (this.businessType === 'domestic') {
        if (!this.to.phone || !`${this.to.phone}`.trim()) {
          throw Error(this.$t('fieldIsRequired', {
            fieldName: this.formatAddressFieldLabel('recipient', 'phone')
          }))
        }
        if (this.validatePhoneBasic(this.to.phone) !== true) {
          throw Error(this.$t('fieldIsPhone'))
        }
      }
    },
    validateExportCommodities () {
      if (this.businessType !== 'export') return
      if (!Array.isArray(this.commodities) || this.commodities.length < 1) {
        throw Error(this.$t('atLeastOneCommodity'))
      }
      for (let item of this.commodities) {
        if (!item.commodity || !item.commodity.trim()) {
          throw Error(this.$t('commodityDescriptionIsMissing'))
        }
        if (!item.description || !item.description.trim()) {
          throw Error(this.$t('commodityDescriptionIsMissing'))
        }
        if ((item.description || '').trim().length > 100) {
          throw Error(this.$t('commodityDescriptionIsTooLong'))
        }
        if (!item.countryOfOrigin) {
          throw Error(this.$t('countryOfOriginValidation'))
        }
        if (!item.harmonizedCode) {
          throw Error(this.$t('harmonizedCodeIsMissing'))
        }
        if (this.validateHsCode(item.harmonizedCode) !== true) {
          throw Error(this.$t('hsCodeValidation'))
        }
        if (!(Number(item.quantity) > 0) || Number(item.quantity) % 1 !== 0) {
          throw Error(this.$t('quantityIsMissing'))
        }
        if (!item.unit || !item.unit.trim()) {
          throw Error(this.$t('fieldIsRequired', { fieldName: 'Unit' }))
        }
        if (Number(item.price) < 0) {
          throw Error(this.$t('fieldIsNoLessThanZero', { fieldName: 'Price' }))
        }
        // if (Number(item.customsValue) < 0) {
        //   throw Error(this.$t('fieldIsNoLessThanZero', { fieldName: 'Customs value' }))
        // }
        if (!(Number(item.commodityWeight) > 0)) {
          throw Error(this.$t('fieldIsOverZero', { fieldName: 'Weight' }))
        }
      }
    },
    validateHazmatBasics () {
      const { dangerousGoods, hazMatsInfo, dryIceDetail = {} } = this
      if (dangerousGoods === 'BATTERY' && (!Array.isArray(hazMatsInfo) || hazMatsInfo.length < 1)) {
        throw Error(this.$t('fillInAtLeastOneHazMatInformation'))
      }
      if (dangerousGoods === 'BATTERY') {
        if (!this.offerorName || !`${this.offerorName}`.trim()) {
          throw Error(this.$t('fieldIsRequired', { fieldName: 'Offeror name' }))
        }
        if (!this.emergencyContactNumber || !`${this.emergencyContactNumber}`.trim()) {
          throw Error(this.$t('fieldIsRequired', { fieldName: 'Emergency contact number' }))
        }
      }
      if (dangerousGoods === 'DRY_ICE') {
        if (!(Number(dryIceDetail.weight) > 0)) {
          throw Error(this.$t('fieldIsOverZero', { fieldName: 'Weight' }))
        }
        if (!dryIceDetail.unit) {
          throw Error(this.$t('fieldIsRequired', { fieldName: 'Unit' }))
        }
      }
    },
    validDangerousGoodsInfo () {
      const {
        isSupportDangerousGoods,
        hazMatsInfo,
        dangerousGoods,
        packages,
        shippingService,
        labelServices,
        dryIceDetail: {
          weight,
          unit
        }
      } = this
      if (!isSupportDangerousGoods) return

      const isSupportHazmatService = labelServices.some(service => {
        const serviceType = service.serviceTypes.find(serviceType => shippingService === serviceType.value)
        return serviceType && serviceType.isSupportDangerousGoods
      })
      if (!isSupportHazmatService) throw Error(this.$t('shippingServiceNotSupportDangerousGoods'))

      const isBattery = dangerousGoods === 'BATTERY'
      if (isBattery) {
        if (hazMatsInfo.length < 1) throw Error(this.$t('fillInAtLeastOneHazMatInformation'))
        if (this.$refs.HazardousCommodities.totalBatteryWeight > packages[0].weightLb) throw Error(this.$t('dangerousGoodsExceedsWeight'))
      }
      if (dangerousGoods === 'DRY_ICE' && packages[0].weightLb < +this.convertWeight(weight, unit === 'KG')) {
        throw Error(this.$t('dangerousGoodsExceedsWeight'))
      }
    },
    extractPayload (isLithiumBattery) {
      let {
        signature,
        from,
        to,
        businessType,
        shippingService,
        name,
        dangerousGoods,
        hazMatsInfo,
        offerorName,
        emergencyContactNumber,
        isSupportDangerousGoods,
        isSupportLabelStockType,
        labelStockType,
        dryIceDetail,
        packages,
        commodities
      } = this

      const payload = {
        signature,
        isMeasurementMetric: this.isMeasurementMetric,
        from,
        to,
        businessType,
        shippingService: shippingService || '',
        name: name.trim(),
        key: this.initTemplate.key || '',
        packages: packages.map(pkg => ({
          qty: pkg.qty || 1,
          length: pkg.length || 0,
          width: pkg.width || 0,
          height: pkg.height || 0,
          weight: pkg.weight || 0,
          memo: pkg.memo || '',
          isLithiumBattery: pkg.isLithiumBattery || false,
          lithiumBatteryOptions: pkg.lithiumBatteryOptions || [],
          insuredValue: this.enableInsured ? pkg.insuredValue || 0 : 0
        })),
        isSupportHazmat: isSupportDangerousGoods
      }
      if (businessType === 'export') {
        payload.commodities = (commodities || []).map(item => ({
          commodity: item.commodity || '',
          description: item.description || '',
          countryOfOrigin: item.countryOfOrigin || 'US',
          harmonizedCode: item.harmonizedCode || '',
          quantity: Number(item.quantity) || 0,
          unit: item.unit || '',
          price: Number(item.price) || 0,
          customsValue: this.computeCustomsValue({ quantity: item.quantity, price: item.price }) || 0,
          commodityWeight: Number(item.commodityWeight) || 0
        }))
      }
      if (isSupportLabelStockType) payload.labelStockType = labelStockType
      else payload.labelStockType = 'STOCK_4X6'
      if (isSupportDangerousGoods) {
        payload.dangerousGoods = dangerousGoods
        const isBattery = dangerousGoods === 'BATTERY'
        if (isBattery) {
          Object.assign(payload, {
            hazMatsInfo,
            offerorName,
            emergencyContactNumber
          })
        } else if (dangerousGoods === 'LITHIUM_BATTERY') {
          if (this.isPrintSafetyDocument && !this.signatureCode) {
            throw Error('Please select signature file for dangerous goods.')
          }
          if (!payload.packages.some(item => item.lithiumBatteryOptions && item.lithiumBatteryOptions.length > 0)) {
            throw Error('Please select lithium battery options for at least one package.')
          }
          Object.assign(payload, {
            isSupportDangerousGoods,
            dangerousGoods,
            signatureCode: this.isPrintSafetyDocument && this.signatureCode ? this.signatureCode.downloadURL : null,
            isPrintSafetyDocument: this.isPrintSafetyDocument || false,
            packages: packages.map(item => {
              return {
                ...item,
                isLithiumBattery: item.lithiumBatteryOptions && item.lithiumBatteryOptions.length > 0,
                lithiumBatteryOptions: item.lithiumBatteryOptions || []
              }
            })
          })
        } else if (dangerousGoods === 'DRY_ICE') {
          Object.assign(payload, {
            dryIceDetail,
            packages: packages.map(item => {
              return {
                ...item,
                isLithiumBattery: false,
                lithiumBatteryOptions: []
              }
            })
          })
        } else {
          Object.assign(payload, {
            isSupportDangerousGoods,
            dangerousGoods,
            packages: packages.map(item => {
              return {
                ...item,
                isLithiumBattery: false,
                lithiumBatteryOptions: []
              }
            })
          })
        }
      } else {
        payload.packages = packages.map(item => {
          return {
            ...item,
            isLithiumBattery: false,
            lithiumBatteryOptions: []
          }
        })
      }
      return payload
    },
    onSubmitted () {
      const { businessType, dangerousGoods, isSupportDangerousGoods } = this
      const isLithiumBattery = dangerousGoods === 'LITHIUM_BATTERY'
      this.validateAddressBasics()
      if (businessType === 'export') {
        this.validateExportCommodities()
      }
      if (isSupportDangerousGoods) {
        this.validateHazmatBasics()
      }
      this.validDangerousGoodsInfo()
      if (isSupportDangerousGoods && isLithiumBattery && this.isPrintSafetyDocument && !this.signatureCode) {
        throw Error('Please select signature file for dangerous goods.')
      }
      const payload = this.extractPayload(isLithiumBattery)
      return this.$store.dispatch('createOrUpdateLabelTemplate', payload)
        .then(() => {
          this.$emit('submitted', payload)
        })
    },
    getLabelServiceSettingByCarrierAndServiceTypeValue (carrier, serviceTypeValue) {
      return this.$store.getters.getLabelServiceSettingByCarrierAndServiceTypeValue(carrier, serviceTypeValue)
    },
    ensureShippingServiceForBusinessType () {
      const services = (this.businessType === 'export'
        ? (this.shippingServices || [])
        : this.filterShippingServiceByFromState(this.shippingServices || [], this.from.state)
      ).filter(item => !item.divider)
      const hasCurrentService = services.some(service => service.value === this.shippingService)
      if (hasCurrentService) return
      this.shippingService = (services.find(service => service.default) || services[0] || {}).value || ''
    }
  },
  props: {
    value: Boolean,
    initTemplate: Object
  }
}
</script>

<style>
.table-item {
  padding-bottom: 12px;
}

.template-header-card {
  margin-bottom: 12px;
}

.template-header-hint {
  padding-top: 8px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 13px;
  line-height: 20px;
}
</style>



// WEBPACK FOOTER //
// src/components/LabelTemplatePopupForLabel.vue